'use client';

import React, { useEffect, useState, useRef, useMemo } from 'react';
import { supabase } from '../../supabaseClient';
import MeasurementsMap from '../../components/MeasurementMap';
import * as Chart from 'chart.js';
import {
  ChartBarIcon,
  CheckCircleIcon,
  FireIcon,
  BeakerIcon,
  CubeIcon,
  CalendarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/20/solid';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

// Register Chart.js components
Chart.Chart.register(
  Chart.CategoryScale,
  Chart.LinearScale,
  Chart.PointElement,
  Chart.LineElement,
  Chart.Title,
  Chart.Tooltip,
  Chart.Legend
);

const MetricTrendChart = ({ data, metric, height = 250 }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  
  const metricConfig = {
    compaction: {
      label: 'Compaction',
      color: '#10b981',
      unit: '%',
      threshold: 92
    },
    density: {
      label: 'Density',
      color: '#3b82f6',
      unit: ' pcf',
      threshold: null
    },
    temperature: {
      label: 'Surface Temperature',
      color: '#f59e0b',
      unit: '°F',
      threshold: null
    },
    voids: {
      label: 'Voids',
      color: '#8b5cf6',
      unit: '%',
      threshold: null
    }
  };
  
  const config = metricConfig[metric] || metricConfig.compaction;
  
  useEffect(() => {
    if (chartRef.current && data && data.length > 0) {
      // Destroy existing chart
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
      
      const ctx = chartRef.current.getContext('2d');
      
      // Determine label format based on data size
      const showLabels = data.length <= 50; // Only show labels if not too many points
      
      // Prepare datasets
      const datasets = [{
        label: config.label,
        data: data.map(d => d.value),
        borderColor: config.color,
        backgroundColor: config.color + '20',
        borderWidth: 2,
        tension: 0.4, // Smooth line
        fill: true,
        pointRadius: data.length > 100 ? 0 : 2, // Hide points if too many
        pointHoverRadius: 5,
        pointBackgroundColor: config.color,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 1
      }];
      
      // Add threshold line if applicable
      if (config.threshold) {
        datasets.push({
          label: `Threshold (${config.threshold}${config.unit})`,
          data: new Array(data.length).fill(config.threshold),
          borderColor: '#ef4444',
          backgroundColor: 'transparent',
          borderWidth: 1,
          borderDash: [5, 5],
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false
        });
      }
      
      chartInstance.current = new Chart.Chart(ctx, {
        type: 'line',
        data: {
          labels: data.map((d, idx) => {
            if (!showLabels && idx % Math.ceil(data.length / 20) !== 0) {
              return ''; // Show fewer labels if too many points
            }
            const date = new Date(d.datetime);
            return `${date.getMonth() + 1}/${date.getDate()}`;
          }),
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false,
          },
          plugins: {
            legend: { 
              display: true,
              position: 'bottom',
              labels: {
                boxWidth: 12,
                padding: 15,
                font: { size: 11 }
              }
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              padding: 12,
              titleFont: { size: 12 },
              bodyFont: { size: 11 },
              // This filter function ensures the tooltip only shows for the main data series
              filter: function(tooltipItem) {
                // The main data is datasetIndex 0, and the threshold line is datasetIndex 1.
                // We only want to show the tooltip for the main data.
                return tooltipItem.datasetIndex === 0;
              },
              callbacks: {
                title: function(context) {
                  // Because of the filter, context[0] will always refer to the main dataset.
                  const dataPoint = data[context[0].dataIndex];
                  const date = new Date(dataPoint.datetime);
                  return date.toLocaleString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                },
                label: function(context) {
                  // Because of the filter, this callback only runs for the main dataset.
                  const dataPoint = data[context.dataIndex];
                  let labels = [];
                  labels.push(`${config.label}: ${context.parsed.y.toFixed(1)}`);
                  if (dataPoint.operator) {
                    labels.push(`Operator: ${dataPoint.operator}`);
                  }
                  return labels;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: false,
              ticks: {
                callback: function(value) {
                  return value.toFixed(1) + config.unit;
                },
                font: { size: 11 }
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.05)'
              }
            },
            x: {
              grid: {
                display: false
              },
              ticks: {
                font: { size: 11 },
                maxRotation: 45,
                minRotation: 0,
                autoSkip: true,
                maxTicksLimit: 20
              }
            }
          }
        }
      });
    }
    
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data, metric, config]);
  
  return <canvas ref={chartRef} height={height} />;
};

export default function ProjectOverview({ 
  projectId, 
  measurements, 
  memberIds,
  projectData,
  projectPolygon = null
}) {
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapMeasurements, setMapMeasurements] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('compaction');
  const [timeRange, setTimeRange] = useState('all'); // Default to all data
  const [dateRange, setDateRange] = useState({ min: null, max: null, days: 0 });
  const [timeRangeOptions, setTimeRangeOptions] = useState([]);
  const [selectedMapMeasurement, setSelectedMapMeasurement] = useState(null);
const [mapCenter, setMapCenter] = useState({ lat: 42.73116, lng: -73.68657 });

  
  // Calculate summary statistics
  useEffect(() => {
    const calculateSummary = async () => {
      if (!projectId || !memberIds || memberIds.length === 0) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      
      try {
        // Fetch all measurements for this project
        const { data, error } = await supabase
          .from('measurements_with_details')
          .select('*')
          .eq('project_id', projectId)
          .in('profile_id', memberIds)
          .order('LocalTimeOfMeasurement', { ascending: true }); // Chronological order
        
        if (error) throw error;
        
        if (!data || data.length === 0) {
          setSummaryData(null);
          setLoading(false);
          return;
        }
        
        // Calculate date range
        const dates = data.map(m => new Date(m.LocalTimeOfMeasurement));
        const minDate = new Date(Math.min(...dates));
        const maxDate = new Date(Math.max(...dates));
        const totalDays = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24));
        
        setDateRange({ 
          min: minDate, 
          max: maxDate, 
          days: totalDays 
        });
        
        // Generate smart time range options based on data range
        const options = [];
        options.push({ value: 'all', label: 'All Time' });
        
        // Only show "last X days" if we're within that range of today
        const today = new Date();
        const daysSinceLastMeasurement = Math.ceil((today - maxDate) / (1000 * 60 * 60 * 24));
        
        if (daysSinceLastMeasurement < 7 && totalDays > 7) {
          options.push({ value: '7', label: 'Last 7 Days' });
        }
        if (daysSinceLastMeasurement < 30 && totalDays > 30) {
          options.push({ value: '30', label: 'Last 30 Days' });
        }
        if (daysSinceLastMeasurement < 90 && totalDays > 90) {
          options.push({ value: '90', label: 'Last 3 Months' });
        }
        if (daysSinceLastMeasurement < 180 && totalDays > 180) {
          options.push({ value: '180', label: 'Last 6 Months' });
        }
        if (daysSinceLastMeasurement < 365 && totalDays > 365) {
          options.push({ value: '365', label: 'Last Year' });
        }
        
        // Add year options if data spans multiple years
        if (totalDays > 365) {
          const years = [...new Set(dates.map(d => d.getFullYear()))].sort();
          years.forEach(year => {
            options.push({ value: `year-${year}`, label: year.toString() });
          });
        }
        
        // Add month options if data is within a year
        if (totalDays <= 365 && totalDays > 30) {
          const months = [...new Set(dates.map(d => `${d.getFullYear()}-${d.getMonth()}`))];
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          months.forEach(monthKey => {
            const [year, month] = monthKey.split('-');
            options.push({ 
              value: `month-${monthKey}`, 
              label: `${monthNames[parseInt(month)]} ${year}` 
            });
          });
        }
        
        setTimeRangeOptions(options);
        
        // Set map measurements (with coordinates)
        const validMapMeasurements = data.filter(m => m.latitude && m.longitude);
        setMapMeasurements(validMapMeasurements);

        if (validMapMeasurements.length > 0) {
        setMapCenter({
            lat: validMapMeasurements[0].latitude,
            lng: validMapMeasurements[0].longitude
        });
        }
        // Calculate basic stats
        const totalMeasurements = data.length;
        const passingTests = data.filter(m => m.Compaction >= 92).length;
        const passRate = ((passingTests / totalMeasurements) * 100).toFixed(1);
        
        const avgTemp = (data.reduce((sum, m) => sum + (m.SurfaceTemp || 0), 0) / data.length).toFixed(1);
        const avgCompaction = (data.reduce((sum, m) => sum + (m.Compaction || 0), 0) / data.length).toFixed(1);
        const avgDensity = (data.reduce((sum, m) => sum + (m.Density || 0), 0) / data.length).toFixed(1);

        const compactionValues = data.map(m => m.Compaction).filter(v => v != null);
        const compactionRange = compactionValues.length > 0 ? {
          min: Math.min(...compactionValues).toFixed(1),
          max: Math.max(...compactionValues).toFixed(1)
        } : null;
        
        // Calculate temperature range
        const tempValues = data.map(m => m.SurfaceTemp).filter(v => v != null);
        const tempRange = tempValues.length > 0 ? {
          min: Math.min(...tempValues).toFixed(0),
          max: Math.max(...tempValues).toFixed(0)
        } : null;
        
        // Calculate density standard deviation
        const densityValues = data.map(m => m.Density).filter(v => v != null);
        const densityMean = densityValues.reduce((sum, v) => sum + v, 0) / densityValues.length;
        const densityVariance = densityValues.reduce((sum, v) => sum + Math.pow(v - densityMean, 2), 0) / densityValues.length;
        const densityStdDev = Math.sqrt(densityVariance).toFixed(1);
        const densityRange = densityValues.length > 0 ? {
          min: Math.min(...densityValues).toFixed(1),
          max: Math.max(...densityValues).toFixed(1)
        } : null;

        // Get last test time
        const lastTestTime = data.length > 0 ? data[data.length - 1].LocalTimeOfMeasurement : null;
        
        // Count today's tests
        today.setHours(0, 0, 0, 0);
        const todayTests = data.filter(m => {
          const testDate = new Date(m.LocalTimeOfMeasurement);
          testDate.setHours(0, 0, 0, 0);
          return testDate.getTime() === today.getTime();
        }).length;
        
        // Calculate operator counts
        const operatorMap = {};
        data.forEach(m => {
          if (m.operator_name) {
            operatorMap[m.operator_name] = (operatorMap[m.operator_name] || 0) + 1;
          }
        });
        
        const operatorCounts = Object.entries(operatorMap)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10); // Top 10 operators
        
        const maxOperatorCount = operatorCounts.length > 0 ? operatorCounts[0].count : 0;
        
        // Store all measurements for trend calculations
        setSummaryData({
          totalMeasurements,
          passRate,
          avgTemp,
          avgCompaction,
          avgDensity,
          operatorCounts,
          maxOperatorCount,
          allMeasurements: data,
          tempRange,
          compactionRange,
          densityStdDev,
          densityRange,
          lastTestTime,
          todayTests
        });
        
      } catch (err) {
        console.error('Error calculating summary:', err);
        setSummaryData(null);
      } finally {
        setLoading(false);
      }
    };
    
    calculateSummary();
  }, [projectId, memberIds]);
  
  // Calculate trend data - individual measurements
  const trendData = useMemo(() => {
    if (!summaryData || !summaryData.allMeasurements) return [];
    
    let filteredData = [...summaryData.allMeasurements];
    
    // Apply time range filter
    if (timeRange !== 'all') {
      if (timeRange.startsWith('year-')) {
        // Filter by specific year
        const year = parseInt(timeRange.split('-')[1]);
        filteredData = filteredData.filter(m => 
          new Date(m.LocalTimeOfMeasurement).getFullYear() === year
        );
      } else if (timeRange.startsWith('month-')) {
        // Filter by specific month
        const [year, month] = timeRange.split('-').slice(1);
        filteredData = filteredData.filter(m => {
          const date = new Date(m.LocalTimeOfMeasurement);
          return date.getFullYear() === parseInt(year) && date.getMonth() === parseInt(month);
        });
      } else {
        // Filter by days from today
        const days = parseInt(timeRange);
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - days);
        
        filteredData = filteredData.filter(
          m => new Date(m.LocalTimeOfMeasurement) >= cutoffDate
        );
      }
    }
    
    if (filteredData.length === 0) return [];
    
    // Get the metric key
    const metricKey = {
      compaction: 'Compaction',
      density: 'Density',
      temperature: 'SurfaceTemp',
      voids: 'Voids'
    }[selectedMetric];
    
    // Return individual measurements
    return filteredData
      .filter(m => m[metricKey] !== null && m[metricKey] !== undefined)
      .map(m => ({
        datetime: m.LocalTimeOfMeasurement,
        value: m[metricKey],
        operator: m.operator_name
      }))
      .sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
    
  }, [summaryData, selectedMetric, timeRange]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">Loading overview...</div>
      </div>
    );
  }
  
  if (!summaryData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">No data available</div>
      </div>
    );
  }
  
 
    return (
  <div className="space-y-4">
    {/* Main Container - All Components */}
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header Section */}
      {dateRange.min && (
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 overflow-hidden rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-900">
                Project Timeline
              </span>
              
            </div>
            
            {/* Quick Info */}
            <div className="flex items-center gap-4 text-xs text-gray-600">
              <span className="text-sm text-gray-600 ml-2">
                {dateRange.min.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} 
                {' - '}
                {dateRange.max.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                <span className="ml-2 text-gray-400">
                  ({dateRange.days} days)
                </span>
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Stats Grid */}
<div className="px-4 pt-4">
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
    {/* Total Tests Card */}
    <div className="bg-white rounded-lg p-4  border border-gray-200">
      <div className="flex items-center gap-2 mb-1">
        <ChartBarIcon className="h-4 w-4 text-blue-500" />
        <span className="text-xs text-gray-500">Total</span>
      </div>
      <div className="text-2xl font-bold text-gray-900">
        {summaryData.totalMeasurements.toLocaleString()}
      </div>
      <div className="text-xs text-gray-600">
        Measurements
      </div>
      <div className="text-xs text-gray-500 mt-1">
        Avg {Math.round(summaryData.totalMeasurements / Math.max(dateRange.days, 1))}/day
      </div>
    </div>

    {/* Compaction Card */}
    <div className="bg-white rounded-lg p-4  border border-gray-200">
      <div className="flex items-center gap-2 mb-1">
        <BeakerIcon className={`h-4 w-4 ${
          parseFloat(summaryData.avgCompaction) >= 92 ? 'text-green-500' : 'text-amber-500'
        }`} />
        <span className="text-xs text-gray-500">Compaction</span>
      </div>
      <div className="text-2xl font-bold text-gray-900">
        {summaryData.avgCompaction}
        <span className="text-lg font-normal text-gray-500">%</span>
      </div>
      <div className="text-xs text-gray-600">
        Avg Compaction
      </div>
      <div className="text-xs text-gray-500 mt-1">
        Range {summaryData.compactionRange ? `${summaryData.compactionRange.min} - ${summaryData.compactionRange.max}` : 'N/A'}
      </div>
    </div>
    
    {/* Temperature Card */}
    <div className="bg-white rounded-lg p-4  border border-gray-200">
      <div className="flex items-center gap-2 mb-1">
        <FireIcon className="h-4 w-4 text-orange-500" />
        <span className="text-xs text-gray-500">Temp</span>
      </div>
      <div className="text-2xl font-bold text-gray-900">
        {summaryData.avgTemp}
      </div>
      <div className="text-xs text-gray-600">
        Avg Temperature
      </div>
      <div className="text-xs text-gray-500 mt-1">
        Range {summaryData.tempRange ? `${summaryData.tempRange.min} - ${summaryData.tempRange.max}` : 'N/A'}
      </div>
    </div>
    
    {/* Density Card */}
    <div className="bg-white rounded-lg p-4  border border-gray-200">
      <div className="flex items-center gap-2 mb-1">
        <CubeIcon className="h-4 w-4 text-purple-500" />
        <span className="text-xs text-gray-500">Density</span>
      </div>
      <div className="text-2xl font-bold text-gray-900">
        {summaryData.avgDensity}
      </div>
      <div className="text-xs text-gray-600">
        Avg Density
      </div>
      <div className="text-xs text-gray-500 mt-1">
        Range {summaryData.densityRange.min || 'N/A'} - {summaryData.densityRange.max || 'N/A'}
      </div>
    </div>

    {/* Pass Rate Card */}
    <div className="bg-white rounded-lg p-4  border border-gray-200">
      <div className="flex items-center gap-2 mb-1">
        <CheckCircleIcon className={`h-4 w-4 ${
          parseFloat(summaryData.passRate) >= 92 ? 'text-green-500' : 'text-red-500'
        }`} />
        <span className="text-xs text-gray-500">Quality</span>
      </div>
      <div className={`text-2xl font-bold ${
        parseFloat(summaryData.passRate) >= 92 ? 'text-green-600' : 'text-red-600'
      }`}>
        {summaryData.passRate}%
      </div>
      <div className="text-xs text-gray-600">
        Pass Rate
      </div>
      <div className="text-xs mt-1">
        {parseFloat(summaryData.passRate) >= 92 ? (
          <span className="text-green-600">↑ Above 92%</span>
        ) : (
          <span className="text-red-600">↓ Below 92%</span>
        )}
      </div>
    </div>
  </div>
</div>
      
      {/* Map and Operator Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 border-b border-gray-200">
        {/* Map */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="h-[400px]">
            <MeasurementsMap
                measurements={mapMeasurements}
                selectedMeasurement={selectedMapMeasurement}
                geofencePolygon={projectPolygon}
                mapCenter={mapCenter}
                mapZoom={13}
                onMarkerClick={(measurement) => setSelectedMapMeasurement(measurement.id)}
                showStyleControls={true}
                showMeasurementCount={true}
                height="400px"
                />
          </div>
        </div>
        
        {/* Operator Chart - Capped at 4 operators */}
<div className="border border-gray-200 rounded-lg p-4">
  <h3 className="text-sm font-semibold text-gray-900 mb-4">Tests by Operator</h3>
  
  {summaryData.operatorCounts.length > 0 ? (
    <>
      {/* Operators - Capped at 4 */}
      <div className="space-y-3 mb-4">
        {summaryData.operatorCounts.slice(0, 4).map((op, index) => {
          const widthPercent = (op.count / summaryData.maxOperatorCount) * 100;
          const colors = [
            'bg-blue-500',
            'bg-green-500',
            'bg-purple-500',
            'bg-orange-500'
          ];
          const color = colors[index % colors.length];
          
          return (
            <div key={op.name} className="flex items-center gap-3">
              <div className="w-32 text-xs text-gray-700 truncate" title={op.name}>
                {op.name || 'Unknown'}
              </div>
              <div className="flex-1 flex items-center gap-2">
                <div className="flex-1 relative">
                  <div 
                    className={`h-2 ${color} rounded-full transition-all duration-500 ease-out`}
                    style={{ 
                      width: `${Math.max(widthPercent, 2)}%`
                    }}
                  />
                </div>
                <span className="text-sm font-semibold text-gray-700 ml-2">
                  {op.count}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Show more link if there are more than 4 operators */}
      {summaryData.operatorCounts.length > 4 && (
        <div className="text-xs text-gray-500 mb-4">
          +{summaryData.operatorCounts.length - 4} more operators
        </div>
      )}
      
      {/* Activity Timeline */}
      <div className="border-t border-gray-200 pt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Recent Activity</h4>
        <div className="space-y-2">
          {(() => {
            // Group measurements by day
            const dailyActivity = {};
            summaryData.allMeasurements.forEach(m => {
              const date = new Date(m.LocalTimeOfMeasurement);
              const dateKey = date.toISOString().split('T')[0];
              if (!dailyActivity[dateKey]) {
                dailyActivity[dateKey] = {
                  date: date,
                  count: 0,
                  operators: new Set()
                };
              }
              dailyActivity[dateKey].count++;
              if (m.operator_name) {
                dailyActivity[dateKey].operators.add(m.operator_name);
              }
            });
            
            // Get the last 4 days with activity
            const sortedDays = Object.entries(dailyActivity)
              .sort((a, b) => b[0].localeCompare(a[0]))
              .slice(0, 4);
            
            return sortedDays.map(([dateKey, data]) => {
              const isToday = dateKey === new Date().toISOString().split('T')[0];
              const isYesterday = dateKey === new Date(Date.now() - 86400000).toISOString().split('T')[0];
              
              const dayLabel = isToday ? 'Today' : 
                             isYesterday ? 'Yesterday' : 
                             data.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
              
              // Calculate width percentage based on max daily count
              const maxDailyCount = Math.max(...sortedDays.map(([_, d]) => d.count));
              const widthPercent = (data.count / maxDailyCount) * 100;
              
              return (
                <div key={dateKey} className="flex items-center gap-3">
                  <div className="w-20 text-xs text-gray-600">
                    {dayLabel}
                  </div>
                  <div className="flex-1 flex items-center gap-2">
                    <div className="flex-1 bg-gray-100 rounded-full h-2 relative overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${
                          isToday ? 'bg-blue-400' : 'bg-gray-400'
                        }`}
                        style={{ width: `${widthPercent}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-700 min-w-[30px] text-right">
                      {data.count}
                    </span>
                  </div>
                  
                </div>
              );
            });
          })()}
        </div>
      </div>
    </>
  ) : (
    <div className="text-center text-gray-500 py-8">
      No operator data available
    </div>
  )}
</div>
      </div>
      
      {/* Trend Chart */}
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-semibold text-gray-900">
            Trend Analysis
            <span className="ml-2 text-xs font-normal text-gray-500">
              ({trendData.length} measurements)
            </span>
          </h3>
          <div className="flex gap-2">
            {/* Metric Dropdown */}
            <Menu as="div" className="relative inline-block">
              <MenuButton className="inline-flex items-center justify-between gap-x-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 min-w-[120px]">
                <span>
                  {selectedMetric === 'compaction' && 'Compaction'}
                  {selectedMetric === 'density' && 'Density'}
                  {selectedMetric === 'temperature' && 'Temperature'}
                  {selectedMetric === 'voids' && 'Voids'}
                </span>
                <ChevronDownIcon aria-hidden="true" className="-mr-1 h-4 w-4 text-gray-400" />
              </MenuButton>
              <MenuItems
                transition
                className="absolute left-0 z-10 mt-2 w-40 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[enter]:ease-out data-[leave]:duration-75 data-[leave]:ease-in"
              >
                <div className="py-1">
                  <MenuItem>
                    <button
                      onClick={() => setSelectedMetric('compaction')}
                      className={`block w-full px-4 py-2 text-left text-sm ${
                        selectedMetric === 'compaction' 
                          ? 'bg-gray-100 text-gray-900' 
                          : 'text-gray-700'
                      } data-[focus]:bg-gray-100 data-[focus]:text-gray-900`}
                    >
                      Compaction
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button
                      onClick={() => setSelectedMetric('density')}
                      className={`block w-full px-4 py-2 text-left text-sm ${
                        selectedMetric === 'density' 
                          ? 'bg-gray-100 text-gray-900' 
                          : 'text-gray-700'
                      } data-[focus]:bg-gray-100 data-[focus]:text-gray-900`}
                    >
                      Density
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button
                      onClick={() => setSelectedMetric('temperature')}
                      className={`block w-full px-4 py-2 text-left text-sm ${
                        selectedMetric === 'temperature' 
                          ? 'bg-gray-100 text-gray-900' 
                          : 'text-gray-700'
                      } data-[focus]:bg-gray-100 data-[focus]:text-gray-900`}
                    >
                      Temperature
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button
                      onClick={() => setSelectedMetric('voids')}
                      className={`block w-full px-4 py-2 text-left text-sm ${
                        selectedMetric === 'voids' 
                          ? 'bg-gray-100 text-gray-900' 
                          : 'text-gray-700'
                      } data-[focus]:bg-gray-100 data-[focus]:text-gray-900`}
                    >
                      Voids
                    </button>
                  </MenuItem>
                </div>
              </MenuItems>
            </Menu>

            {/* Time Range Dropdown */}
            <Menu as="div" className="relative inline-block">
              <MenuButton className="inline-flex items-center justify-between gap-x-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 min-w-[140px]">
                <span>
                  {timeRangeOptions.find(opt => opt.value === timeRange)?.label || 'All Time'}
                </span>
                <ChevronDownIcon aria-hidden="true" className="-mr-1 h-4 w-4 text-gray-400" />
              </MenuButton>
              <MenuItems
                transition
                className="absolute right-0 z-10 mt-2 w-44 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[enter]:ease-out data-[leave]:duration-75 data-[leave]:ease-in"
              >
                <div className="py-1 max-h-60 overflow-auto">
                  {timeRangeOptions.map(option => (
                    <MenuItem key={option.value}>
                      <button
                        onClick={() => setTimeRange(option.value)}
                        className={`block w-full px-4 py-2 text-left text-sm ${
                          timeRange === option.value 
                            ? 'bg-gray-100 text-gray-900' 
                            : 'text-gray-700'
                        } data-[focus]:bg-gray-100 data-[focus]:text-gray-900`}
                      >
                        {option.label}
                      </button>
                    </MenuItem>
                  ))}
                </div>
              </MenuItems>
            </Menu>
          </div>
        </div>
        
        {trendData.length > 0 ? (
          <div style={{ height: '300px' }}>
            <MetricTrendChart 
              data={trendData}
              metric={selectedMetric}
              height={300}
            />
          </div>
        ) : (
          <div className="text-center text-gray-500 py-12">
            No data available for selected time range
          </div>
        )}
      </div>
    </div>
  </div>
);
}